import {
    db,
    restaurantsCollection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    collection,
    GeoPoint,
    storage,
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
  } from './firebase';
  
  // Get all restaurants
  export const getRestaurants = async () => {
    const snapshot = await getDocs(restaurantsCollection);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  };
  
  // Get a single restaurant by ID
  export const getRestaurantById = async (id) => {
    const docRef = doc(db, 'Restaurants', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Restaurant not found');
    }
  };
  
  // Create a new restaurant
  export const createRestaurant = async (restaurantData) => {
    const { latitude, longitude, ...rest } = restaurantData;
    
    // Create GeoPoint for Firebase
    const latlng = new GeoPoint(parseFloat(latitude), parseFloat(longitude));

    //Ensure socialMedia is an object
    const socialMedia = restaurantData.socialMedia || {};
    
    const data = {
      ...rest,
      latlng,
      socialMedia,
      created_at: serverTimestamp(),
      modified_at: serverTimestamp()
    };
    
    const docRef = await addDoc(restaurantsCollection, data);
    return docRef.id;
  };
  
  // Update an existing restaurant
  export const updateRestaurant = async (id, restaurantData) => {
    const docRef = doc(db, 'Restaurants', id);
    
    // Handle potential lat/lng update
    let dataToUpdate = { ...restaurantData };
    
    if (restaurantData.latitude && restaurantData.longitude) {
      const { latitude, longitude, ...rest } = restaurantData;
      const latlng = new GeoPoint(parseFloat(latitude), parseFloat(longitude));
      dataToUpdate = {
        ...rest,
        latlng
      };
    }
    
    // Always update modified timestamp
    dataToUpdate.modified_at = serverTimestamp();
    
    await updateDoc(docRef, dataToUpdate);
    return id;
  };
  
  // Delete a restaurant
  export const deleteRestaurant = async (id) => {
    const docRef = doc(db, 'Restaurants', id);
    await deleteDoc(docRef);
    return id;
  };
  
  // Upload a photo for a restaurant
  export const uploadRestaurantPhoto = async (restaurantId, file) => {
    const storageRef = ref(storage, `Restaurants/${restaurantId}/photos/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    // Add the photo URL to the restaurant's photoUrls array
    const restaurantRef = doc(db, 'Restaurants', restaurantId);
    const restaurant = await getDoc(restaurantRef);
    
    if (restaurant.exists()) {
      const photoUrls = restaurant.data().photoUrls || [];
      await updateDoc(restaurantRef, {
        photoUrls: [...photoUrls, downloadURL],
        modified_at: serverTimestamp()
      });
    }
    
    return downloadURL;
  };
  
  // Upload logo photo
  export const uploadLogoPhoto = async (restaurantId, file) => {
    const storageRef = ref(storage, `Restaurants/${restaurantId}/logo/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    // Update the restaurant with the new logo URL
    const restaurantRef = doc(db, 'Restaurants', restaurantId);
    await updateDoc(restaurantRef, {
      logoPhotoURL: downloadURL,
      modified_at: serverTimestamp()
    });
    
    return downloadURL;
  };
  
  // Delete a photo
  export const deleteRestaurantPhoto = async (restaurantId, photoUrl) => {
    // Extract the file path from the URL
    const fileRef = ref(storage, photoUrl);
    
    try {
      // Delete from storage
      await deleteObject(fileRef);
      
      // Remove from restaurant document
      const restaurantRef = doc(db, 'Restaurants', restaurantId);
      const restaurant = await getDoc(restaurantRef);
      
      if (restaurant.exists()) {
        const photoUrls = restaurant.data().photoUrls || [];
        await updateDoc(restaurantRef, {
          photoUrls: photoUrls.filter(url => url !== photoUrl),
          modified_at: serverTimestamp()
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error deleting photo:", error);
      throw error;
    }
  };
  
  // MENU ITEMS OPERATIONS - UPDATED VERSION

// Get all menu items for a restaurant
export const getMenuItems = async (restaurantId) => {
  const menuItemsCollection = collection(db, 'Restaurants', restaurantId, 'menuItems');
  const snapshot = await getDocs(menuItemsCollection);
  
  // Log the retrieved data for debugging
  console.log(`Retrieved ${snapshot.docs.length} menu items from Firebase`);
  
  const items = snapshot.docs.map(doc => {
    const data = doc.data();
    console.log(`Menu item ${doc.id}:`, data);
    return {
      id: doc.id,
      ...data,
      // Ensure isAvailable is always a boolean
      isAvailable: data.isAvailable === undefined ? true : Boolean(data.isAvailable)
    };
  });
  
  return items;
};

// Add a menu item
export const addMenuItem = async (restaurantId, menuItemData) => {
  const menuItemsCollection = collection(db, 'Restaurants', restaurantId, 'menuItems');
  
  // Explicitly ensure isAvailable is a boolean
  const dataToSave = {
    ...menuItemData,
    isAvailable: Boolean(menuItemData.isAvailable),
    created_at: serverTimestamp()
  };
  
  console.log("Adding menu item with data:", dataToSave);
  
  const docRef = await addDoc(menuItemsCollection, dataToSave);
  console.log(`Added menu item with ID: ${docRef.id}`);
  
  return docRef.id;
};

// Update a menu item
export const updateMenuItem = async (restaurantId, menuItemId, menuItemData) => {
  const menuItemRef = doc(db, 'Restaurants', restaurantId, 'menuItems', menuItemId);
  
  // Explicitly ensure isAvailable is a boolean
  const dataToUpdate = {
    ...menuItemData,
    isAvailable: Boolean(menuItemData.isAvailable),
    updated_at: serverTimestamp()
  };
  
  console.log(`Updating menu item ${menuItemId} with data:`, dataToUpdate);
  
  await updateDoc(menuItemRef, dataToUpdate);
  console.log(`Menu item ${menuItemId} updated successfully`);
  
  return menuItemId;
};

// Delete a menu item
export const deleteMenuItem = async (restaurantId, menuItemId) => {
  const menuItemRef = doc(db, 'Restaurants', restaurantId, 'menuItems', menuItemId);
  console.log(`Deleting menu item with ID: ${menuItemId}`);
  
  await deleteDoc(menuItemRef);
  console.log(`Menu item ${menuItemId} deleted successfully`);
  
  return menuItemId;
};
  
  // HOURS OPERATIONS
  
  // Get hours for a restaurant
  export const getHours = async (restaurantId) => {
    const hoursCollection = collection(db, 'Restaurants', restaurantId, 'hours');
    const snapshot = await getDocs(hoursCollection);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  };
  
  // Add or update hours
  export const updateHours = async (restaurantId, hoursData) => {
    // First, we'll clear all existing hours
    const hoursCollection = collection(db, 'Restaurants', restaurantId, 'hours');
    const snapshot = await getDocs(hoursCollection);
    
    // Delete all existing hours
    const deletePromises = snapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    await Promise.all(deletePromises);
    
    // Add new hours
    const addPromises = Object.entries(hoursData).map(([day, hours]) => 
      addDoc(hoursCollection, {
        day,
        ...hours,
        updated_at: serverTimestamp()
      })
    ); 
    
    await Promise.all(addPromises);
    return true;
  };